import { Ref } from 'vue';
import { ResizeResult } from './types';
import { debounce } from './util';

import * as tau from 'tau-time';

export const useResizeObserver = (
  object: Ref<HTMLElement | null>,
  callback?: () => void,
  includeWindowResizeEvents = false,
  debounceDelay = tau.seconds(1)
): Ref<Readonly<ResizeResult | null>> => {
  const resizeResult = ref<ResizeResult | null>(null);

  const onResize = () => {
    if (object.value) {
      resizeResult.value = {
        width: object.value.offsetWidth,
        height: object.value.offsetHeight,
      };
    } else {
      resizeResult.value = null;
    }
  };

  const resizeObserver = new ResizeObserver(() => {
    onResize();
    if (callback) callback();
  });

  const resizeEvent = debounce(onResize, debounceDelay);

  if (getCurrentInstance()) {
    let observe: HTMLElement | null = null;

    onUnmounted(() => {
      resizeObserver.disconnect();
    });

    if (includeWindowResizeEvents) {
      onMounted(() => {
        window.addEventListener('resize', resizeEvent);
      });

      onUnmounted(() => {
        window.removeEventListener('resize', resizeEvent);
      });
    }

    watchEffect(() => {
      if (object.value) {
        if (observe != object.value) {
          if (observe) resizeObserver.unobserve(observe);
          resizeObserver.observe(object.value);
        }
      } else {
        if (observe) resizeObserver.unobserve(observe);
      }
      observe = object.value;
      onResize();
    });
  }

  onResize();

  return resizeResult;
};
